import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  BarChart3, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Users, 
  Calendar, 
  TrendingUp, 
  AlertTriangle, 
  Info, 
  Loader2, 
  Eye, 
  FileText, 
  User, 
  Building,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';
import apiService from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import LoteDisparosTable from '@/components/LoteDisparosTable';

interface ClientMetricsDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  metricType: 'pendentes' | 'enviados' | 'invalidos' | 'falhas' | 'baixados';
  title: string;
  icon: React.ComponentType<any>;
  color: string;
  period?: string; // Filtro de período do dashboard
  customStartDate?: string; // Data de início personalizada
  customEndDate?: string; // Data de fim personalizada
}

interface ClientData {
  client_id: number;
  client_name: string;
  count: number;
  percentage: number;
  value?: number;
}

interface BatchData {
  lote_id: string;
  client_name: string;
  count: number;
  percentage: number;
  status: string;
  created_at: string;
  value?: number;
  client_id: number;
}

interface DetailedBatchData {
  lote_id: string;
  client_name: string;
  client_id: number;
  count: number;
  percentage: number;
  status: string;
  created_at: string;
  value?: number;
  error_details?: Array<{
    motivo_rejeicao: string;
    count: number;
    total_value: number;
  }>;
}

interface DetailedData {
  clients: ClientData[];
  batches: DetailedBatchData[];
  total: number;
  summary: {
    total_clients: number;
    total_batches: number;
    average_per_client: number;
    top_client: string;
    success_rate?: number;
    failure_rate?: number;
  };
}

const ClientMetricsDetailModal: React.FC<ClientMetricsDetailModalProps> = ({
  isOpen,
  onClose,
  metricType,
  title,
  icon: Icon,
  color,
  period,
  customStartDate,
  customEndDate
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DetailedData | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<DetailedBatchData | null>(null);
  const [showBatchDetails, setShowBatchDetails] = useState(false);

  const fetchDetailedData = async () => {
    setLoading(true);
    try {
      // Construir parâmetros da query
      const params = new URLSearchParams();
      
      console.log('🔍 [ClientMetricsDetailModal] Parâmetros recebidos:', {
        period,
        customStartDate,
        customEndDate,
        metricType,
        userClientId: user?.client_id
      });
      
      if (period === 'custom' && customStartDate && customEndDate) {
        params.append('start_date', customStartDate);
        params.append('end_date', customEndDate);
        console.log('📅 [ClientMetricsDetailModal] Usando datas customizadas:', { customStartDate, customEndDate });
      } else {
        params.append('period', period || 'today');
        console.log('📅 [ClientMetricsDetailModal] Usando período:', period || 'today');
      }
      
      // O backend já filtra automaticamente por cliente baseado no usuário logado
      // Não precisamos enviar client_id explicitamente

      // Mapear tipos de métrica para endpoints (baseado nos endpoints disponíveis no backend)
      const endpointMap: { [key: string]: string } = {
        'pendentes': 'pendentes',
        'enviados': 'enviados',
        'invalidos': 'invalidos',
        'falhas': 'falhas',
        'baixados': 'baixados'
      };

      const endpoint = endpointMap[metricType];
      const url = `/analytics/disparos/detailed/${endpoint}?${params}`;
      
      console.log('🌐 [ClientMetricsDetailModal] Fazendo requisição para:', url);
      console.log('🔍 [ClientMetricsDetailModal] Parâmetros finais:', Object.fromEntries(params));
      
      const response = await apiService.get(url);

      console.log('📊 [ClientMetricsDetailModal] Resposta da API:', response);

      if (response.success && response.data) {
        setData(response.data);
        console.log('✅ [ClientMetricsDetailModal] Dados carregados com sucesso:', response.data);
      } else {
        console.error('❌ [ClientMetricsDetailModal] Erro ao carregar dados:', response.error);
      }
    } catch (error) {
      console.error('❌ [ClientMetricsDetailModal] Erro ao buscar dados detalhados:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDetailedData();
    }
  }, [isOpen, metricType, period, customStartDate, customEndDate]);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Data não disponível';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error('Data inválida:', dateString);
        return 'Data inválida';
      }
      
      return date.toLocaleDateString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return 'Data inválida';
    }
  };

  const handleViewBatchDetails = (batch: DetailedBatchData) => {
    console.log('📊 [ClientMetricsDetailModal] Abrindo detalhes do lote:', batch);
    setSelectedBatch(batch);
    setShowBatchDetails(true);
  };

  const handleCloseBatchDetails = () => {
    console.log('📊 [ClientMetricsDetailModal] Fechando detalhes do lote');
    setSelectedBatch(null);
    setShowBatchDetails(false);
  };

  const getMetricIcon = () => {
    const iconMap = {
      'pendentes': Clock,
      'enviados': CheckCircle2,
      'invalidos': XCircle,
      'falhas': AlertCircle,
      'baixados': TrendingUp
    };
    return iconMap[metricType] || BarChart3;
  };

  const getMetricColor = () => {
    const colorMap = {
      'pendentes': 'text-yellow-600',
      'enviados': 'text-green-600',
      'invalidos': 'text-red-600',
      'falhas': 'text-orange-600',
      'baixados': 'text-blue-600'
    };
    return colorMap[metricType] || 'text-gray-600';
  };

  const MetricIcon = getMetricIcon();

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${color}`}>
              <MetricIcon className="w-6 h-6 text-white" />
            </div>
            Detalhes de {title}
          </DialogTitle>
          <DialogDescription>
            Análise detalhada dos dados de {title.toLowerCase()} com informações por cliente e lote
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
            <span className="ml-2 text-gray-600">Carregando dados...</span>
          </div>
        ) : data ? (
          <div className="space-y-6">
            {/* Resumo Geral */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Resumo Geral
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {formatNumber(data.total)}
                    </div>
                    <div className="text-sm text-gray-600">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {data.summary.total_clients}
                    </div>
                    <div className="text-sm text-gray-600">Clientes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {data.summary.total_batches}
                    </div>
                    <div className="text-sm text-gray-600">Lotes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {formatNumber(data.summary.average_per_client)}
                    </div>
                    <div className="text-sm text-gray-600">Média/Cliente</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* {title} por Cliente e Lote */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  {title} por Cliente e Lote
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {data.batches.map((batch, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-4">
                        <Badge variant="outline" className="bg-gray-100 text-gray-700">
                          #{index + 1}
                        </Badge>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="font-medium">{batch.client_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            Lote: #{batch.lote_id} • Criado em: {formatDate(batch.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-lg font-bold text-gray-900">
                            {formatNumber(batch.count)} {title.toLowerCase()}
                          </div>
                          {batch.value && (
                            <div className="text-sm text-green-600">
                              {formatCurrency(batch.value)}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewBatchDetails(batch)}
                          className="flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          Ver Detalhes
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">Nenhum dado disponível para este período</p>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={onClose} variant="outline">
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Modal Aninhado - Detalhes do Lote */}
    <Dialog open={showBatchDetails} onOpenChange={handleCloseBatchDetails}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseBatchDetails}
              className="p-2"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Detalhes do Lote #{selectedBatch?.lote_id}
              </DialogTitle>
              <DialogDescription>
                Cliente: {selectedBatch?.client_name} | {formatNumber(selectedBatch?.count || 0)} registros
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {selectedBatch && (
          <div className="space-y-6">
            {/* Resumo Rápido */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Info className="w-5 h-5" />
                  Informações do Lote
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">
                      {formatNumber(selectedBatch.count)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Total de Registros</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      #{selectedBatch.lote_id}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Número do Lote</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-lg font-bold text-gray-900">
                      {selectedBatch.client_name}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Cliente</div>
                  </div>
                  {selectedBatch.value && (
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-xl font-bold text-green-600">
                        {formatCurrency(selectedBatch.value)}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">Valor Total</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Tabela de Disparos */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Detalhes dos Disparos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  console.log('🔍 [ClientMetricsDetailModal] Passando props para LoteDisparosTable:', {
                    loteId: selectedBatch.lote_id,
                    metricType,
                    clientId: selectedBatch.client_id,
                    initialStatusFilter: selectedBatch.status
                  });
                  return (
                    <LoteDisparosTable 
                      loteId={selectedBatch.lote_id} 
                      metricType={metricType}
                      clientId={selectedBatch.client_id}
                      initialStatusFilter={selectedBatch.status}
                    />
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  </>
  );
};

export default ClientMetricsDetailModal;
